import { Suspense, lazy, useEffect, useRef, useState } from "react";
import type { ComponentProps, ComponentType } from "react";
import { Controller } from "react-hook-form";
import type BasePhoneInputType from "react-phone-number-input/input";

import { Input } from "../Input/Input";
import type { InputProps } from "../Input/Input";
import { PhoneInputWithFlag } from "../Input/PhoneInputWithFlag";

// Explicit annotation avoids TS2742: inferred LazyExoticComponent would reference the
// pnpm-internal @types/react path in generated .d.ts output.
const BasePhoneInput: ComponentType<ComponentProps<typeof BasePhoneInputType>> =
  lazy(() => import("react-phone-number-input/input"));

type PhoneInputProps = {
  control: any;
} & Omit<InputProps, "value" | "onChange" | "onBlur"> &
  Pick<
    ComponentProps<typeof BasePhoneInput>,
    "withCountryCallingCode" | "country"
  >;

// Workaround for "Maximum update depth exceeded" loop when fast typing.
// https://github.com/catamphetamine/react-phone-number-input/issues/441
// TODO: Remove once the library supports controlled component behaviour.
// https://gitlab.com/catamphetamine/react-phone-number-input/-/work_items/249
type LocallyControlledPhoneInputProps = {
  value: string | null;
  onChange: (value?: string) => void;
} & Record<string, any>;

const LocallyControlledPhoneInput = ({
  value,
  onChange,
  ...rest
}: LocallyControlledPhoneInputProps) => {
  const [localValue, setLocalValue] = useState<string>(value ?? "");
  const lastEmittedValue = useRef<string>(value ?? "");

  useEffect(() => {
    const incoming = value ?? "";
    if (incoming !== lastEmittedValue.current) {
      lastEmittedValue.current = incoming;
      setLocalValue(incoming);
    }
  }, [value]);

  const handleChange = (next?: string) => {
    lastEmittedValue.current = next ?? "";
    setLocalValue(next ?? "");
    onChange(next);
  };

  return (
    <BasePhoneInput {...rest} value={localValue} onChange={handleChange} />
  );
};

const PhoneInput = ({ control, name, label, ...props }: PhoneInputProps) => {
  return (
    <Suspense fallback={<Input loading label={label} />}>
      <Controller
        control={control}
        name={name as string}
        render={({ field }) => (
          <LocallyControlledPhoneInput
            {...field}
            {...props}
            label={label}
            inputComponent={
              props.withCountryCallingCode ? PhoneInputWithFlag : Input
            }
            international
            {...(props.withCountryCallingCode
              ? { countryCode: props.country }
              : {})}
          />
        )}
      />
    </Suspense>
  );
};

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
export type { PhoneInputProps };
